import React, { useState, useEffect, useCallback } from 'react';
import {
  Table, Button, Space, Tag, message, Modal, Form, InputNumber,
  DatePicker, Input, Select, Popconfirm, Row, Col, Card, Statistic, Alert,
} from 'antd';
import { ThunderboltOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  getPayments, generatePayments, updatePayment, deletePayment,
} from '../../api/preschools';
import { formatMoney } from '../../utils/money';
import { apiErrorMessage } from '../../utils/apiError';

const AYLAR = [
  '', 'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık',
];

const DURUM = {
  odendi:   <Tag color="green">Ödendi</Tag>,
  kismi:    <Tag color="blue">Kısmi</Tag>,
  gecikti:  <Tag color="red">Gecikti</Tag>,
  bekliyor: <Tag color="default">Bekliyor</Tag>,
};

/**
 * Anaokulu tahsilatı — ay bazında, SABİT aylık tutar.
 *
 * KURUMUN NORMAL ÖDEME AKIŞIYLA HİÇBİR BAĞI YOK: Finans raporlarına
 * karışmaz, `payments` tablosuna yazmaz (kullanıcının açık talimatı).
 *
 * KESİNTİ YOK (kullanıcı kararı): yapılmayan ders aylık tutarı
 * değiştirmiyor. Gerekirse ay bazında tutar elle düzeltilebiliyor.
 */
const PreschoolPaymentsTab = ({ agreement }) => {
  const [rows, setRows] = useState([]);
  const [ozet, setOzet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [duzenle, setDuzenle] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const yukle = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getPayments(agreement.id);
      setRows(data.data || []);
      setOzet(data.ozet || null);
    } catch {
      message.error('Ödemeler yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, [agreement.id]);

  useEffect(() => { yukle(); }, [yukle]);

  const planUret = async () => {
    setSaving(true);
    try {
      const { data } = await generatePayments(agreement.id, {});
      message.success(data.message);
      yukle();
    } catch (error) {
      message.error(apiErrorMessage(error, 'Plan üretilemedi.'));
    } finally {
      setSaving(false);
    }
  };

  const kaydet = async (values) => {
    setSaving(true);
    try {
      await updatePayment(agreement.id, duzenle.id, {
        paid_amount: values.paid_amount,
        // Tarih .format('YYYY-MM-DD') ile gonderilir (CLAUDE.md).
        paid_at: values.paid_at ? values.paid_at.format('YYYY-MM-DD') : null,
        method: values.method || null,
        note: values.note || null,
        amount: values.amount,
      });
      message.success('Tahsilat kaydedildi.');
      setDuzenle(null);
      yukle();
    } catch (error) {
      message.error(apiErrorMessage(error, 'Kaydedilemedi.'));
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      /*
        DERS BAZLI SATIRDA SADECE AY YAZMAK YANILTICI olurdu: o satır bir
        takvim ayını değil, N ders günü dolan bir dönemi temsil ediyor ve
        dönem iki aya yayılabiliyor (ör. 17.09 – 06.10). Aynı ayda iki
        dönem de dolabiliyor (haftada iki gün gidilen okulda), o yüzden
        "Ekim 2026" iki satırda birden görünürdü.

        Aylık satırlarda eski görünüm aynen kalıyor.
      */
      title: 'Dönem',
      key: 'ay',
      render: (_, r) => {
        if (r.basis !== 'lesson_count') return `${AYLAR[r.month]} ${r.year}`;
        const g = (t) => (t ? dayjs(t).format('DD.MM.YYYY') : '—');
        return (
          <span>
            {g(r.period_start)} – {g(r.period_end)}
            <Tag color="blue" className="ml-2">{r.lesson_count} ders</Tag>
          </span>
        );
      },
    },
    {
      title: 'Vade',
      dataIndex: 'due_date',
      render: (v) => dayjs(v).format('DD.MM.YYYY'),
    },
    {
      title: 'Tutar',
      dataIndex: 'amount',
      align: 'right',
      render: (v) => formatMoney(v),
    },
    {
      title: 'Tahsil',
      dataIndex: 'paid_amount',
      align: 'right',
      render: (v, r) => (
        <Space direction="vertical" size={0} style={{ alignItems: 'flex-end' }}>
          <span>{formatMoney(v)}</span>
          {r.paid_at && <span className="text-xs text-gray-400">{dayjs(r.paid_at).format('DD.MM.YYYY')}</span>}
        </Space>
      ),
    },
    {
      title: 'Durum',
      key: 'durum',
      render: (_, r) => DURUM[r.durum] ?? DURUM.bekliyor,
    },
    {
      title: '',
      key: 'islem',
      width: 130,
      render: (_, r) => (
        <Space>
          <Button
            size="small"
            onClick={() => {
              setDuzenle(r);
              form.setFieldsValue({
                amount: r.amount,
                paid_amount: r.paid_amount,
                paid_at: r.paid_at ? dayjs(r.paid_at) : null,
                method: r.method,
                note: r.note,
              });
            }}
          >
            Tahsilat
          </Button>
          <Popconfirm
            title="Ay silinsin mi?"
            onConfirm={async () => {
              try {
                await deletePayment(agreement.id, r.id);
                message.success('Silindi.');
                yukle();
              } catch (error) {
                message.error(apiErrorMessage(error, 'Silinemedi.'));
              }
            }}
            okText="Sil"
            cancelText="Vazgeç"
          >
            <Button danger size="small" icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {ozet && (
        <Row gutter={16} className="mb-4">
          <Col xs={12} md={6}><Card size="small"><Statistic title="Planlanan" value={formatMoney(ozet.toplam)} /></Card></Col>
          <Col xs={12} md={6}><Card size="small"><Statistic title="Tahsil" value={formatMoney(ozet.tahsil)} /></Card></Col>
          <Col xs={12} md={6}><Card size="small"><Statistic title="Kalan" value={formatMoney(ozet.kalan)} /></Card></Col>
          <Col xs={12} md={6}>
            <Card size="small">
              <Statistic title="Gecikmiş" value={ozet.gecikmis} valueStyle={ozet.gecikmis > 0 ? { color: '#cf1322' } : undefined} />
            </Card>
          </Col>
        </Row>
      )}

      <div className="flex justify-between items-center mb-3 flex-wrap gap-2">
        <span className="text-gray-500 text-sm">
          {/*
            METİN MODA GÖRE DEĞİŞMEK ZORUNDA. "Yapılamayan ders tutarı
            değiştirmez" cümlesi yalnızca AYLIK modda doğru; ders bazlı
            modda yapılmayan ders dönemi doldurmuyor, yani doğrudan
            faturalamayı etkiliyor. Tek bir sabit cümle bırakmak iki
            moddan birinde kullanıcıya yanlış bilgi vermek olurdu.
          */}
          {agreement.payment_basis === 'lesson_count'
            ? `${agreement.lessons_per_payment || 4} ders günü yapıldıkça bir aylık tutar borç yazılır; `
              + 'yapılmayan ders dönemi doldurmaz. Dönem dolmadan satır oluşmaz.'
            : 'Aylık tutar sabittir; yapılamayan ders tutarı değiştirmez.'}
        </span>
        <Button icon={<ThunderboltOutlined />} onClick={planUret} loading={saving}>
          Ödeme Planı Üret
        </Button>
      </div>

      <Table
        rowKey="id"
        size="small"
        loading={loading}
        dataSource={rows}
        columns={columns}
        pagination={false}
        locale={{ emptyText: 'Ödeme planı üretilmemiş. "Ödeme Planı Üret" ile anlaşma süresince aylık satırlar oluşturulur.' }}
      />

      <Modal
        title={duzenle ? `${AYLAR[duzenle.month]} ${duzenle.year} — Tahsilat` : ''}
        open={!!duzenle}
        onCancel={() => setDuzenle(null)}
        footer={null}
      >
        <Alert
          type="info"
          showIcon
          className="mb-3"
          message="Tutar bu ay için dondurulmuştur"
          description="Anlaşmanın aylık ücreti sonradan değişse de bu satır etkilenmez. Gerekirse tutarı burada elle düzeltebilirsiniz."
        />
        <Form form={form} layout="vertical" onFinish={kaydet}>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="amount" label="Tutar (₺)">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="paid_amount" label="Tahsil Edilen (₺)" rules={[{ required: true }]}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="paid_at" label="Tahsilat Tarihi">
                <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="method" label="Yöntem">
                <Select
                  allowClear
                  options={['Nakit', 'Havale/EFT', 'Kredi Kartı', 'Çek'].map((m) => ({ value: m, label: m }))}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="note" label="Not"><Input.TextArea rows={2} /></Form.Item>
          <Button type="primary" htmlType="submit" loading={saving} block>Kaydet</Button>
        </Form>
      </Modal>
    </div>
  );
};

export default PreschoolPaymentsTab;
